import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import MyAccountTemplate from "../../sections/my-account-template";
import "./style.scss";
import { Heading, PrimaryButton, BodyText } from "../../elements";
import api from "../../utils/api";
import { selectUserState } from "../../selectors/application";
import { connect } from "react-redux";
import { isGlobalSite } from "../../utils/siteUtils";
import { genrateHeadingWithFirstName } from "../../utils/myAccount";
import { setUserInfoAction } from "../../redux/actions";
import MyAddressItem from "../../sections/my-address-item";
import AccountRemoveAddressModal from "../../sections/account-remove-address-modal";
import RadioButtonGroup from "../../elements/radio-button-group";
import AccountPasswordSetting from "../../sections/account-password-setting";
import AccountEmailSetting from "../../sections/account-email-setting";
import AccountPhoneNumberSetting from "../../sections/account-phone-number-setting";
import AccountProfileSetting from "../../sections/account-profile-setting";
import Icon from "../../elements/icon";
import { getCountryOptions } from "../../services/siteService";
import getSiteInfo from "../../utils/getSiteInfo";

function MyAccoutPersonalInformation({
  lang,
  user,
  setUserInfo,
  fields: {
    hero,
    accountMenu,
    contentHeadingType,
    contentHeadingBody,
    contentBody,
    firstNameLabel,
    firstNameMissingMessage,
    lastNameLabel,
    lastNameMissingMessage,
    birthdayLabel,
    birthdayWarningMessage,
    birthdayUnder18Message,
    phoneAreaLabel,
    phoneLabel,
    phoneMissingMessage,
    phoneExistMessage,
    phoneWarningMessage,
    streetLabel,
    apartmentLabel,
    postalCodeLabel,
    cityLabel,
    countryLabel,
    countryMissingMessage,
    ctaNameSaveChange,
    colorSchemeSaveChange,
    iconNameSaveChange,
    updateFailedMessage,
    updateSuccessMessage,
    addressItemFields,
    removeAddressModal,
    changeEmailInfo,
    changePhoneNumberInfo,
    changePassword,
    resetPassword,
    profileSetting,
    selectVerificationMethodModal
  }
}) {
  const { newAddressLabel } = addressItemFields.fields;
  const [countryList, setCountryList] = useState([]);
  const [addressList, setAddressList] = useState([]);
  const [selectedPrimaryAddress, setSelectedPrimaryAddress] = useState(null);
  const [predefinedCountry, setPredefinedCountry] = useState(null);
  const [removeAddressId, setRemoveAddressId] = useState(null);
  const [isCTADisabled, setIsCTADisabled] = useState(true);
  const [showAddressRemoveModal, setShowAddressRemoveModal] = useState(false);
  const addressFormRef = useRef({});
  useEffect(() => {
    initCountries();
    loadAddressList();
  }, []);
  const initCountries = async () => {
    const options = await getCountryOptions();
    setCountryList(options);
    if (!isGlobalSite()) {
      const siteInfo = await getSiteInfo();
      const country = options.find(x => x.code === siteInfo.country);
      setPredefinedCountry(country);
    }
  };
  const loadAddressList = () => {
    api.address.list().then(({ obj }) => {
      let _addressList = obj.map(address => {
        return { address: address, value: address.addressId };
      });
      const primaryAddress = _addressList.find(x => x.address.defaultFlag == "1");
      setSelectedPrimaryAddress(primaryAddress);
      _addressList.push({ label: { value: newAddressLabel.value }, value: "new" });
      setAddressList(_addressList || []);
    });
  };

  const formatSavedAddress = address => {
    let street = `${address?.street || ""} ${address?.apartment || ""}`;
    let zipCity = `${address?.postalCode || ""} ${address?.city || ""}`;
    return [street, zipCity];
  };

  const addressProps = {
    firstNameLabel,
    lastNameLabel,
    birthdayLabel,
    streetLabel,
    apartmentLabel,
    postalCodeLabel,
    cityLabel,
    countryLabel,
    addressItemFields: addressItemFields.fields,
    countries: countryList
  };
  // const messageProps = {
  //   className: classNames(hasServerError ? "failed-message" : "success-message"),
  //   html: hasServerError ? updateFailedMessage.value : updateSuccessMessage.value
  // };

  const removeAddressModalProps = {
    ...removeAddressModal.fields,
    showModal: showAddressRemoveModal,
    onModalClose: evt => {
      setRemoveAddressId(null);
      setShowAddressRemoveModal(false);
    },
    onSubmit: async evt => {
      await api.address.delete(null, {
        addressId: removeAddressId
      });
      const _addressList = addressList.filter(x => x.value !== removeAddressId);
      setAddressList(_addressList);
      setRemoveAddressId(null);
      setShowAddressRemoveModal(false);
    }
  };

  const onRemoveClick = (evt, option) => {
    evt.stopPropagation();
    const { value } = option;
    setRemoveAddressId(value);
    setShowAddressRemoveModal(true);
  };

  const addressLayout = option => {
    if (option.address) {
      return (
        <div className="saved-address-block" key={option.address.id}>
          <div className="body-container">
            {formatSavedAddress(option.address).map((row, index) => {
              return (
                <p className="content-row" key={index}>
                  {row}
                </p>
              );
            })}
          </div>
          <div className="icons">{option.address.defaultFlag != "1" && <Icon name="Delete" colorScheme="outline-green" className="icon --visible" onClick={evt => onRemoveClick(evt, option)} />}</div>
        </div>
      );
    } else {
      const address = {
        country: predefinedCountry
      };
      return (
        <MyAddressItem
          {...{
            ...addressProps,
            ...option,
            ref: instance => {
              if (selectedPrimaryAddress) {
                addressFormRef.current[selectedPrimaryAddress.value] = instance;
              }
            },
            isSelectedOption: option?.value === selectedPrimaryAddress?.value
          }}
          address={address}
          key={"new address"}
        />
      );
    }
  };

  const handleChangeRadioButtons = option => {
    setSelectedPrimaryAddress(option);
    setIsCTADisabled(option.address?.defaultFlag == "1");
  };

  const btnProps = addressItemFields.fields.ctaName && {
    buttonText: addressItemFields.fields.ctaName.value,
    colorScheme: addressItemFields.fields.colorScheme,
    iconName: addressItemFields.fields.iconName,
    isDisabled: isCTADisabled,
    callback: async () => {
      if (selectedPrimaryAddress) {
        if (selectedPrimaryAddress.value === "new") {
          const formInstance = addressFormRef.current[selectedPrimaryAddress.value];
          if (formInstance) {
            const { isValid, data } = formInstance.validateForm();
            if (isValid) {
              await api.address.create(null, data);
              loadAddressList();
            }
          }
        } else {
          const data = {
            ...selectedPrimaryAddress.address,
            defaultFlag: "1",
            addressType: 3
          };
          await api.address.update(null, data);
          loadAddressList();
        }
      }
    }
  };
  const headingProps = {
    type: addressItemFields.fields.headingType,
    html: addressItemFields.fields.headingBody,
    className: "heading"
  };
  const changePhoneNumberProps = {
    ...changePhoneNumberInfo,
    fields: {
      ...changePhoneNumberInfo.fields,
      phoneMissingMessage,
      phoneWarningMessage
    },
    verifyEmailForm: changeEmailInfo?.fields?.verifyEmailForm[0],
    selectVerificationMethodModal
  };
  const changeEmailInfoProps = {
    ...changeEmailInfo,
    verifyPhoneNumberForm: changePhoneNumberInfo,
    verifyEmailForm: changeEmailInfo?.fields?.verifyEmailForm[0],
    selectVerificationMethodModal
  };

  return (
    <>
      <MyAccountTemplate
        heroConfiguration={{
          ...hero[0].fields,
          titleBody: genrateHeadingWithFirstName(hero[0].fields.titleBody, user ? user.firstName : "")
        }}
        menu={{ ...accountMenu[0].fields }}
      >
        <div className="my-account-personal-information">
          <div className="my-account-personal-information-content">
            <Heading type={contentHeadingType} typographyClass="type-h3" html={contentHeadingBody} />

            <BodyText html={contentBody.value} className="my-account-personal-information-body" />
          </div>

          <AccountPasswordSetting {...{ ...changePassword, lang }} />
          <AccountEmailSetting {...{ ...changeEmailInfoProps, lang, resetPassword }} />
          <AccountPhoneNumberSetting {...{ ...changePhoneNumberProps, lang, resetPassword }} />
          <AccountProfileSetting {...profileSetting} />

          {addressList.length > 0 && (
            <div className="my-account-personal-information-form">
              <Heading {...headingProps} />
              <RadioButtonGroup options={addressList} html={addressLayout} alignment="top" onChange={e => handleChangeRadioButtons(e)} selectedOption={selectedPrimaryAddress} />
              <PrimaryButton {...btnProps} />
            </div>
          )}
        </div>
      </MyAccountTemplate>
      <AccountRemoveAddressModal {...removeAddressModalProps} />
    </>
  );
}
const mapStateToProps = state => ({
  user: selectUserState(state)
});
const mapDispatchToProps = dispatch => ({
  setUserInfo: data => dispatch(setUserInfoAction(data))
});

export default connect(mapStateToProps, mapDispatchToProps)(MyAccoutPersonalInformation);

MyAccoutPersonalInformation.propTypes = {
  heroConfiguration: PropTypes.object,
  menu: PropTypes.object,
  heading: PropTypes.object,
  body: PropTypes.string,
  firstName: PropTypes.object,
  lastName: PropTypes.object,
  dateOfBirth: PropTypes.object,
  street1: PropTypes.object,
  street2: PropTypes.object,
  zipInput: PropTypes.object,
  cityInput: PropTypes.object,
  countryInput: PropTypes.object,
  phoneNumberInput: PropTypes.object,
  phoneNumberPrefix: PropTypes.object,
  primaryButton: PropTypes.object,
  serverError: PropTypes.shape({
    hasError: PropTypes.bool,
    message: PropTypes.string
  })
};
